import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  CheckCircle2,
  Cloud,
  Copy,
  ExternalLink,
  Film,
  Globe,
  Image,
  Loader2,
  RefreshCw,
  Settings2,
  Share2,
  Trash2,
  Type,
  UserCog,
  Video,
  Wand2,
  Youtube,
  X,
} from 'lucide-react';
import ShareModal from './ShareModal';
import EditSpeakersModal from './EditSpeakersModal';
import ProductionMetadataFields from './ProductionMetadataFields';
import { SessionService, type YouTubeTitleSyncState } from '../services/sessionService';
import { startTitleGenerationSession } from '../services/agentService';
import type { RetentionData, VideoStats, YouTubeAuthStatus } from '../services/youtubeAnalyticsService';
import * as youtubeAnalyticsService from '../services/youtubeAnalyticsService';
import { SessionVideoSourceStatus, type SpeakerLabels, type SpeakerSegment } from '../types';
import type { VideoSourceType } from './AppHeader';

type SettingsSectionId = 'title' | 'share' | 'speakers' | 'playback' | 'thumbnail' | 'youtube' | 'production';

interface SessionSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: SettingsSectionId;
  isDarkMode: boolean;
  sessionId: string;
  sessionTitle?: string;
  token?: string;
  onTitleUpdate?: (newTitle: string) => void;
  onSessionRefresh?: () => Promise<void> | void;
  youtubeUrl?: string | null;
  youtubeTitle?: string | null;
  youtubeTitleUpdatedAt?: string | null;
  youtubeTitleSync?: YouTubeTitleSyncState | null;
  videoUrl?: string | null;
  videoSource?: VideoSourceType;
  onYoutubeUrlChange?: (url: string | null) => void;
  onVideoUrlChange?: (url: string | null, source: VideoSourceType) => void;
  onRefreshVideoSource?: () => Promise<void> | void;
  videoStats?: VideoStats | null;
  videoDurationSeconds?: number | null;
  fetchedAt?: string | null;
  onRetentionDataUpdate?: (data: RetentionData) => void;
  speakerIds?: number[];
  speakerSegments?: Map<number, SpeakerSegment[]>;
  speakerLabels?: SpeakerLabels;
  onSaveSpeakerLabels?: (labels: SpeakerLabels) => Promise<void>;
  transcriptionChecksum?: string;
  isYouTubeMode?: boolean;
  dropboxVideoUrl?: string | null;
  videoFilename?: string | null;
  dropboxDrpUrl?: string | null;
  timelineName?: string | null;
  localVideoPath?: string | null;
  productionNotes?: string | null;
  videoFileHash?: string | null;
  videoFileSize?: number | null;
  videoFrameRate?: number | null;
  videoDuration?: number | null;
  audioDuration?: number | null;
  videoSourceStatus?: SessionVideoSourceStatus | null;
  customThumbnailUrl?: string | null;
  customThumbnailTimeSeconds?: number | null;
  onProductionMetadataUpdate?: (data: {
    dropboxVideoUrl?: string | null;
    videoFilename?: string | null;
    dropboxDrpUrl?: string | null;
    timelineName?: string | null;
    localVideoPath?: string | null;
    productionNotes?: string | null;
  }) => void;
  onVideoSourceStatusUpdate?: (status: SessionVideoSourceStatus) => void;
  publishedToHomepage?: boolean;
  onPublishChange?: (published: boolean) => void;
}

function normalizeLocalVideoPath(value?: string | null): string {
  if (!value) return '';
  let cleaned = value.trim();
  while (cleaned.length >= 2) {
    const first = cleaned[0];
    const last = cleaned[cleaned.length - 1];
    if ((first === '"' && last === '"') || (first === '\'' && last === '\'') || (first === '`' && last === '`')) {
      cleaned = cleaned.slice(1, -1).trim();
    } else {
      break;
    }
  }
  return cleaned;
}

function getPlaybackLinkLabel(url?: string | null, source?: VideoSourceType): string {
  if (!url) return 'No playback source selected';
  if (source === 'youtube') return 'YouTube';
  if (source === 'dropbox') return 'Dropbox';
  if (source === 'file') return 'Hosted AWS video';
  return 'Video link';
}

function getYouTubeThumbnailUrl(url?: string | null): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
  }
  return null;
}

function detectVideoSource(url: string): VideoSourceType {
  if (!url || !url.trim()) return null;
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  if (youtubePatterns.some((pattern) => pattern.test(url))) return 'youtube';
  const dropboxPattern = /^https?:\/\/(www\.)?dropbox\.com\/(s|scl\/fi)\/[a-zA-Z0-9_-]+/;
  if (dropboxPattern.test(url)) return 'dropbox';
  return null;
}

function formatSyncTime(value?: string | null) {
  if (!value) return 'Not scheduled';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatBytes(bytes?: number | null) {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = -1;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDisplayUrl(value?: string | null) {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    const compactPath = path.length > 30 ? `...${path.slice(-30)}` : path;
    return `${parsed.hostname}${compactPath}`;
  } catch (_) {
    const trimmed = value.trim();
    return trimmed.length > 56 ? `${trimmed.slice(0, 53)}...` : trimmed;
  }
}

function formatMetricNumber(value: number | null | undefined): string {
  if (value == null) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
}

function formatMetricDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '-';
  const totalSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function formatExpiryCountdown(value?: string | null): string {
  if (!value) return 'Expiry unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Expiry unknown';
  const diffMs = parsed.getTime() - Date.now();
  if (diffMs <= 0) return 'Expired';
  const diffMinutes = Math.round(diffMs / (60 * 1000));
  if (diffMinutes < 60) return `Expires in ${diffMinutes}m`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 48) return `Expires in ${diffHours}h`;
  const diffDays = Math.round(diffHours / 24);
  return `Expires in ${diffDays}d`;
}

const SETTINGS_SECTIONS: Array<{ id: SettingsSectionId; title: string; description: string }> = [
  { id: 'title', title: 'Title', description: 'Edit, generate, and sync the session title.' },
  { id: 'share', title: 'Sharing', description: 'Create and manage public share links.' },
  { id: 'speakers', title: 'Speakers', description: 'Rename diarized speakers without leaving settings.' },
  { id: 'playback', title: 'Playback', description: 'Control linked playback and inspect the active source.' },
  { id: 'thumbnail', title: 'Thumbnail', description: 'Pick a durable session thumbnail for homepage cards and social/share unfurls.' },
  { id: 'youtube', title: 'YouTube', description: 'Connect YouTube, refresh analytics, and inspect retention-linked stats.' },
  { id: 'production', title: 'Production', description: 'Store source metadata and manage AWS video prep.' },
];

const SessionSettingsModal: React.FC<SessionSettingsModalProps> = ({
  isOpen,
  onClose,
  initialSection = 'title',
  isDarkMode,
  sessionId,
  sessionTitle,
  token,
  onTitleUpdate,
  onSessionRefresh,
  youtubeUrl,
  youtubeTitle,
  youtubeTitleUpdatedAt,
  youtubeTitleSync,
  videoUrl,
  videoSource,
  onYoutubeUrlChange,
  onVideoUrlChange,
  onRefreshVideoSource,
  videoStats,
  videoDurationSeconds,
  fetchedAt,
  onRetentionDataUpdate,
  speakerIds = [],
  speakerSegments = new Map<number, SpeakerSegment[]>(),
  speakerLabels = {},
  onSaveSpeakerLabels,
  transcriptionChecksum,
  isYouTubeMode = false,
  dropboxVideoUrl,
  videoFilename,
  dropboxDrpUrl,
  timelineName,
  localVideoPath,
  productionNotes,
  videoFileHash,
  videoFileSize,
  videoFrameRate,
  videoDuration,
  audioDuration,
  videoSourceStatus,
  customThumbnailUrl,
  customThumbnailTimeSeconds,
  onProductionMetadataUpdate,
  onVideoSourceStatusUpdate,
  publishedToHomepage = false,
  onPublishChange,
}) => {
  const [activeSection, setActiveSection] = useState<SettingsSectionId>('title');
  const previousSessionIdRef = useRef<string | null>(null);
  const previousIsOpenRef = useRef(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  ));

  const [draftTitle, setDraftTitle] = useState(sessionTitle || '');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState(false);
  const [isSyncingYoutubeTitle, setIsSyncingYoutubeTitle] = useState(false);
  const [isUpdatingTitleSync, setIsUpdatingTitleSync] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [titleSyncEnabled, setTitleSyncEnabled] = useState(!!youtubeTitleSync?.enabled);

  const [videoUrlInput, setVideoUrlInput] = useState(youtubeUrl || '');
  const [detectedSource, setDetectedSource] = useState<VideoSourceType>(youtubeUrl ? 'youtube' : null);
  const [isSavingVideoUrl, setIsSavingVideoUrl] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [youtubeAuthStatus, setYoutubeAuthStatus] = useState<YouTubeAuthStatus | null>(null);
  const [youtubeAuthLoading, setYoutubeAuthLoading] = useState(false);
  const [youtubeAuthBusy, setYoutubeAuthBusy] = useState(false);
  const [youtubeAuthError, setYoutubeAuthError] = useState<string | null>(null);
  const [youtubeNotice, setYoutubeNotice] = useState<string | null>(null);
  const [isRefreshingAnalytics, setIsRefreshingAnalytics] = useState(false);
  const [youtubeAnalyticsError, setYoutubeAnalyticsError] = useState<string | null>(null);

  const [prodDropboxVideoUrl, setProdDropboxVideoUrl] = useState(dropboxVideoUrl || '');
  const [prodVideoFilename, setProdVideoFilename] = useState(videoFilename || '');
  const [prodDropboxDrpUrl, setProdDropboxDrpUrl] = useState(dropboxDrpUrl || '');
  const [prodTimelineName, setProdTimelineName] = useState(timelineName || '');
  const [prodLocalVideoPath, setProdLocalVideoPath] = useState(localVideoPath || '');
  const [prodProductionNotes, setProdProductionNotes] = useState(productionNotes || '');
  const [isSavingProductionMeta, setIsSavingProductionMeta] = useState(false);
  const [productionMetaError, setProductionMetaError] = useState<string | null>(null);
  const [videoSourceStatusState, setVideoSourceStatusState] = useState<SessionVideoSourceStatus | null>(videoSourceStatus || null);
  const [isRefreshingVideoSource, setIsRefreshingVideoSource] = useState(false);
  const [isPreparingVideoSource, setIsPreparingVideoSource] = useState(false);
  const [isDeletingProxy, setIsDeletingProxy] = useState(false);
  const [isTogglingPublish, setIsTogglingPublish] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [customThumbnailUrlState, setCustomThumbnailUrlState] = useState<string | null>(customThumbnailUrl || null);
  const [customThumbnailTimeSecondsState, setCustomThumbnailTimeSecondsState] = useState<number | null>(
    Number.isFinite(customThumbnailTimeSeconds) ? Number(customThumbnailTimeSeconds) : null
  );
  const [thumbnailFrameSeconds, setThumbnailFrameSeconds] = useState<number>(
    Number.isFinite(customThumbnailTimeSeconds) ? Number(customThumbnailTimeSeconds) : 0
  );
  const [isSavingThumbnail, setIsSavingThumbnail] = useState(false);
  const [isClearingThumbnail, setIsClearingThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [thumbnailPreviewReady, setThumbnailPreviewReady] = useState(false);
  const thumbnailVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const isFreshOpen = !previousIsOpenRef.current;
    const sessionChanged = previousSessionIdRef.current !== sessionId;
    if (isFreshOpen || sessionChanged) {
      setActiveSection(initialSection);
    }
    setDraftTitle(sessionTitle || '');
    setTitleError(null);
    setTitleSyncEnabled(!!youtubeTitleSync?.enabled);
    setVideoUrlInput(youtubeUrl || '');
    setDetectedSource(youtubeUrl ? 'youtube' : null);
    setVideoError(null);
    setProdDropboxVideoUrl(dropboxVideoUrl || '');
    setProdVideoFilename(videoFilename || '');
    setProdDropboxDrpUrl(dropboxDrpUrl || '');
    setProdTimelineName(timelineName || '');
    setProdLocalVideoPath(normalizeLocalVideoPath(localVideoPath));
    setProdProductionNotes(productionNotes || '');
    setProductionMetaError(null);
    setVideoSourceStatusState(videoSourceStatus || null);
    setCustomThumbnailUrlState(customThumbnailUrl || null);
    setCustomThumbnailTimeSecondsState(Number.isFinite(customThumbnailTimeSeconds) ? Number(customThumbnailTimeSeconds) : null);
    setThumbnailFrameSeconds(Number.isFinite(customThumbnailTimeSeconds) ? Number(customThumbnailTimeSeconds) : 0);
    setThumbnailError(null);
    setThumbnailPreviewReady(false);
    previousIsOpenRef.current = true;
    previousSessionIdRef.current = sessionId;
  }, [
    isOpen,
    sessionId,
    sessionTitle,
    youtubeTitleSync,
    youtubeUrl,
    dropboxVideoUrl,
    videoFilename,
    dropboxDrpUrl,
    timelineName,
    localVideoPath,
    productionNotes,
    videoSourceStatus,
    customThumbnailUrl,
    customThumbnailTimeSeconds,
    initialSection,
  ]);

  useEffect(() => {
    if (!isOpen) {
      previousIsOpenRef.current = false;
    }
  }, [isOpen]);

  const loadYouTubeAuthStatus = useCallback(async () => {
    if (!token) return;
    setYoutubeAuthLoading(true);
    setYoutubeAuthError(null);
    try {
      const status = await youtubeAnalyticsService.getAuthStatus(token);
      setYoutubeAuthStatus(status);
    } catch (err) {
      setYoutubeAuthError(err instanceof Error ? err.message : 'Failed to load YouTube auth status');
    } finally {
      setYoutubeAuthLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isOpen || !token) return;
    void loadYouTubeAuthStatus();
  }, [isOpen, loadYouTubeAuthStatus, token]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get('youtube_auth');
    const message = params.get('message');
    if (!status && !params.has('session_settings')) return;

    if (status === 'success') {
      setYoutubeNotice('YouTube account connected.');
      setYoutubeAuthError(null);
      void loadYouTubeAuthStatus();
    } else if (status === 'error') {
      setYoutubeAuthError(message || 'YouTube authentication failed');
    }

    params.delete('youtube_auth');
    params.delete('message');
    params.delete('session_settings');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }, [isOpen, loadYouTubeAuthStatus]);

  const inputClassName = `w-full rounded-xl border px-3 py-2 text-sm ${
    isDarkMode
      ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-500'
      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
  } focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30`;

  const panelClassName = `${isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-200 bg-white text-gray-900'}`;

  const buildProductionMetadataPayload = useCallback(() => {
    const normalizedLocalVideoPath = normalizeLocalVideoPath(prodLocalVideoPath);
    return {
      normalizedLocalVideoPath,
      data: {
        dropboxVideoUrl: prodDropboxVideoUrl || null,
        videoFilename: prodVideoFilename || null,
        dropboxDrpUrl: prodDropboxDrpUrl || null,
        timelineName: prodTimelineName || null,
        localVideoPath: normalizedLocalVideoPath || null,
        productionNotes: prodProductionNotes.trim() || null,
      },
    };
  }, [
    prodDropboxDrpUrl,
    prodDropboxVideoUrl,
    prodLocalVideoPath,
    prodProductionNotes,
    prodTimelineName,
    prodVideoFilename,
  ]);

  const hasUnsavedProductionChanges = useMemo(() => {
    const { data } = buildProductionMetadataPayload();
    return (
      (dropboxVideoUrl || null) !== data.dropboxVideoUrl ||
      (videoFilename || null) !== data.videoFilename ||
      (dropboxDrpUrl || null) !== data.dropboxDrpUrl ||
      (timelineName || null) !== data.timelineName ||
      (normalizeLocalVideoPath(localVideoPath) || null) !== data.localVideoPath ||
      ((productionNotes || '').trim() || null) !== data.productionNotes
    );
  }, [
    buildProductionMetadataPayload,
    dropboxVideoUrl,
    videoFilename,
    dropboxDrpUrl,
    timelineName,
    localVideoPath,
    productionNotes,
    prodDropboxVideoUrl,
    prodVideoFilename,
    prodDropboxDrpUrl,
    prodTimelineName,
    prodLocalVideoPath,
    prodProductionNotes,
  ]);

  const awsPrepareSatisfiedByOriginal = !!(
    videoSourceStatusState?.s3Source.exists
    && videoSourceStatusState?.videoMetadata.matchesDefaultProxyProfile
    && !videoSourceStatusState?.proxy.ready
  );

  const currentPlaybackLabel = getPlaybackLinkLabel(videoUrl, videoSource);
  const currentYoutubeThumbnailUrl = getYouTubeThumbnailUrl(youtubeUrl);
  const thumbnailPreviewVideoUrl = (
    (videoSourceStatusState?.playbackSource === 'file' ? (videoSourceStatusState.playbackUrl || null) : null)
    || (videoSource === 'file' ? (videoUrl || null) : null)
  );
  const thumbnailPreviewDuration = Number(
    videoSourceStatusState?.videoMetadata.duration
    ?? videoDuration
    ?? 0
  );
  const effectiveThumbnailUrl = customThumbnailUrlState || currentYoutubeThumbnailUrl || 'https://files.rescript.info/mlst-og-image.png';
  const effectiveThumbnailSourceLabel = customThumbnailUrlState
    ? 'Custom session thumbnail'
    : currentYoutubeThumbnailUrl
      ? 'YouTube default thumbnail'
      : 'Default ReScript image';

  const refreshVideoSourceStatus = async () => {
    if (!sessionId || !token) return;
    setIsRefreshingVideoSource(true);
    setProductionMetaError(null);
    try {
      const status = await SessionService.getVideoSourceStatus(sessionId, token);
      setVideoSourceStatusState(status);
      onVideoSourceStatusUpdate?.(status);
    } catch (err: any) {
      setProductionMetaError(err.message || 'Failed to load video source status');
    } finally {
      setIsRefreshingVideoSource(false);
    }
  };

  const persistProductionMetadata = async (closeOnSuccess: boolean) => {
    if (!sessionId || !token) return;
    setIsSavingProductionMeta(true);
    setProductionMetaError(null);
    try {
      const { normalizedLocalVideoPath, data } = buildProductionMetadataPayload();
      await SessionService.updateProductionMetadata(sessionId, data, token);
      onProductionMetadataUpdate?.(data);
      setProdLocalVideoPath(normalizedLocalVideoPath);
      setProdProductionNotes(data.productionNotes || '');
      if (data.dropboxVideoUrl || data.localVideoPath) {
        await refreshVideoSourceStatus();
      }
      await onSessionRefresh?.();
      if (closeOnSuccess) {
        onClose();
      }
    } catch (err: any) {
      setProductionMetaError(err.message || 'Failed to save production metadata');
      throw err;
    } finally {
      setIsSavingProductionMeta(false);
    }
  };

  const handlePrepareVideoSource = async () => {
    if (!sessionId || !token) return;
    setIsPreparingVideoSource(true);
    setProductionMetaError(null);
    try {
      if (hasUnsavedProductionChanges) {
        await persistProductionMetadata(false);
      }
      const response = await SessionService.prepareVideoSource(sessionId, { proxyMode: 'fast' }, token);
      if (response.videoSourceStatus) {
        setVideoSourceStatusState(response.videoSourceStatus);
        onVideoSourceStatusUpdate?.(response.videoSourceStatus);
      } else {
        await refreshVideoSourceStatus();
      }
      await onRefreshVideoSource?.();
      await onSessionRefresh?.();
    } catch (err: any) {
      setProductionMetaError(err.message || 'Failed to queue AWS video preparation');
    } finally {
      setIsPreparingVideoSource(false);
    }
  };

  const handleDeleteProxy = async () => {
    if (!sessionId || !token) return;
    if (!videoSourceStatusState?.proxy?.s3Key && !videoSourceStatusState?.proxy?.ready) {
      return;
    }
    if (!window.confirm('Delete the prepared AWS proxy for this session? The original source metadata will remain.')) {
      return;
    }

    setIsDeletingProxy(true);
    setProductionMetaError(null);
    try {
      const response = await SessionService.deleteVideoSourceProxy(sessionId, token);
      if (response.videoSourceStatus) {
        setVideoSourceStatusState(response.videoSourceStatus);
        onVideoSourceStatusUpdate?.(response.videoSourceStatus);
      } else {
        await refreshVideoSourceStatus();
      }
      await onRefreshVideoSource?.();
      await onSessionRefresh?.();
    } catch (err: any) {
      setProductionMetaError(err.message || 'Failed to delete AWS proxy');
    } finally {
      setIsDeletingProxy(false);
    }
  };

  const syncThumbnailPreviewToSeconds = useCallback((nextSeconds: number) => {
    const video = thumbnailVideoRef.current;
    if (!video || !Number.isFinite(nextSeconds)) return;
    const durationValue = Number.isFinite(video.duration) ? video.duration : thumbnailPreviewDuration;
    const clampedSeconds = Math.max(
      0,
      Number.isFinite(durationValue) && durationValue > 0
        ? Math.min(nextSeconds, Math.max(0, durationValue - 0.05))
        : nextSeconds
    );
    setThumbnailPreviewReady(false);
    try {
      video.currentTime = clampedSeconds;
    } catch (_) {
      // Ignore invalid seek attempts while metadata is still loading.
    }
  }, [thumbnailPreviewDuration]);

  const handleThumbnailSliderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSeconds = Number(event.target.value);
    setThumbnailFrameSeconds(nextSeconds);
    syncThumbnailPreviewToSeconds(nextSeconds);
  }, [syncThumbnailPreviewToSeconds]);

  const handleSaveThumbnail = useCallback(async () => {
    if (!sessionId || !token) return;
    setIsSavingThumbnail(true);
    setThumbnailError(null);
    try {
      const result = await SessionService.captureHostedThumbnailFrame(sessionId, thumbnailFrameSeconds, token);
      setCustomThumbnailUrlState(result.customThumbnailUrl || null);
      setCustomThumbnailTimeSecondsState(
        Number.isFinite(result.customThumbnailTimeSeconds) ? Number(result.customThumbnailTimeSeconds) : null
      );
      if (Number.isFinite(result.customThumbnailTimeSeconds)) {
        setThumbnailFrameSeconds(Number(result.customThumbnailTimeSeconds));
      }
      await onSessionRefresh?.();
    } catch (err: any) {
      setThumbnailError(err.message || 'Failed to save custom thumbnail');
    } finally {
      setIsSavingThumbnail(false);
    }
  }, [onSessionRefresh, sessionId, thumbnailFrameSeconds, token]);

  const handleClearThumbnail = useCallback(async () => {
    if (!sessionId || !token) return;
    setIsClearingThumbnail(true);
    setThumbnailError(null);
    try {
      await SessionService.clearCustomThumbnail(sessionId, token);
      setCustomThumbnailUrlState(null);
      setCustomThumbnailTimeSecondsState(null);
      await onSessionRefresh?.();
    } catch (err: any) {
      setThumbnailError(err.message || 'Failed to clear custom thumbnail');
    } finally {
      setIsClearingThumbnail(false);
    }
  }, [onSessionRefresh, sessionId, token]);

  useEffect(() => {
    if (!isOpen || activeSection !== 'thumbnail') return;
    if (!thumbnailPreviewVideoUrl) return;
    syncThumbnailPreviewToSeconds(thumbnailFrameSeconds);
  }, [activeSection, isOpen, syncThumbnailPreviewToSeconds, thumbnailFrameSeconds, thumbnailPreviewVideoUrl]);

  if (!isOpen) {
    return null;
  }

  const handleSaveTitle = async () => {
    if (!token || !draftTitle.trim()) return;
    setIsSavingTitle(true);
    setTitleError(null);
    try {
      const response = await SessionService.updateTitle(sessionId, draftTitle.trim(), token);
      onTitleUpdate?.(response.title);
      await onSessionRefresh?.();
    } catch (err: any) {
      setTitleError(err.message || 'Failed to save title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleGenerateTitle = async () => {
    if (!token) return;
    setIsGeneratingTitle(true);
    setTitleError(null);
    try {
      const response = await startTitleGenerationSession({ sessionId }, token);
      window.dispatchEvent(new CustomEvent('navigate-to-agent', {
        detail: { agentSessionId: response.agentSessionId },
      }));
    } catch (err: any) {
      setTitleError(err.message || 'Failed to start title generation');
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!token) return;
    setIsTogglingPublish(true);
    setPublishError(null);
    try {
      const newPublished = !publishedToHomepage;
      await SessionService.toggleHomepagePublish(sessionId, newPublished, token);
      onPublishChange?.(newPublished);
    } catch (err: any) {
      setPublishError(err.message || 'Failed to update publish status');
    } finally {
      setIsTogglingPublish(false);
    }
  };

  const handleSyncYoutubeTitle = async () => {
    if (!token) return;
    setIsSyncingYoutubeTitle(true);
    setTitleError(null);
    try {
      const response = await SessionService.syncTitleFromYouTube(sessionId, {
        applyToSessionTitle: true,
      }, token);
      if (response.sessionTitleChanged) {
        setDraftTitle(response.youtubeTitle);
        onTitleUpdate?.(response.youtubeTitle);
      }
      setTitleSyncEnabled(response.syncState.enabled);
      await onSessionRefresh?.();
    } catch (err: any) {
      setTitleError(err.message || 'Failed to sync title from YouTube');
    } finally {
      setIsSyncingYoutubeTitle(false);
    }
  };

  const handleUpdateYoutubeTitleSync = async (enabled: boolean) => {
    if (!token) return;
    setIsUpdatingTitleSync(true);
    setTitleError(null);
    try {
      const response = await SessionService.updateYoutubeTitleSync(sessionId, enabled, token);
      setTitleSyncEnabled(response.youtubeTitleSync.enabled);
      await onSessionRefresh?.();
    } catch (err: any) {
      setTitleError(err.message || 'Failed to update YouTube title sync');
    } finally {
      setIsUpdatingTitleSync(false);
    }
  };

  const handleSaveVideoUrl = async (nextValue?: string) => {
    if (!sessionId || !token) return;
    const urlToSave = (nextValue ?? videoUrlInput).trim();
    const source = detectVideoSource(urlToSave);

    if (urlToSave && source !== 'youtube') {
      setVideoError('Please enter a valid YouTube URL');
      return;
    }

    setIsSavingVideoUrl(true);
    setVideoError(null);
    try {
      const result = await SessionService.updateVideoUrl(sessionId, urlToSave || null, token);
      const nextYoutubeUrl = result.videoSource === 'youtube' ? (result.youtubeUrl || result.videoUrl || null) : null;
      onYoutubeUrlChange?.(nextYoutubeUrl);
      if (onVideoUrlChange) {
        onVideoUrlChange(result.videoUrl || null, (result.videoSource as any) || null);
      }
      await onRefreshVideoSource?.();
      await onSessionRefresh?.();
    } catch (err: any) {
      setVideoError(err.message || 'Failed to save YouTube URL');
    } finally {
      setIsSavingVideoUrl(false);
    }
  };

  const startYouTubeAuth = async () => {
    if (!token || typeof window === 'undefined') return;
    setYoutubeAuthBusy(true);
    setYoutubeAuthError(null);
    setYoutubeNotice(null);
    try {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('session_settings', 'youtube');
      const authUrl = await youtubeAnalyticsService.getAuthUrl(token, returnUrl.toString());
      window.location.assign(authUrl);
    } catch (err) {
      setYoutubeAuthBusy(false);
      setYoutubeAuthError(err instanceof Error ? err.message : 'Failed to start YouTube authentication');
    }
  };

  const disconnectYouTubeAuth = async () => {
    if (!token) return;
    setYoutubeAuthBusy(true);
    setYoutubeAuthError(null);
    setYoutubeNotice(null);
    try {
      await youtubeAnalyticsService.disconnectYouTube(token);
      setYoutubeNotice('YouTube account disconnected.');
      await loadYouTubeAuthStatus();
    } catch (err) {
      setYoutubeAuthError(err instanceof Error ? err.message : 'Failed to disconnect YouTube');
    } finally {
      setYoutubeAuthBusy(false);
    }
  };

  const handleRefreshAnalytics = async (forceRefresh = true) => {
    if (!token || !sessionId || !youtubeUrl) return;
    setIsRefreshingAnalytics(true);
    setYoutubeAnalyticsError(null);
    setYoutubeNotice(null);
    try {
      const data = await youtubeAnalyticsService.fetchRetentionData(
        sessionId,
        youtubeUrl,
        token,
        forceRefresh
      );
      onRetentionDataUpdate?.(data);
      setYoutubeNotice(data.cached ? 'YouTube analytics refreshed from cache.' : 'YouTube analytics refreshed.');
    } catch (err) {
      if (err instanceof Error && youtubeAnalyticsService.needsAuthentication(err)) {
        setYoutubeAnalyticsError('Connect your YouTube account to refresh analytics for this session.');
      } else {
        setYoutubeAnalyticsError(err instanceof Error ? youtubeAnalyticsService.getErrorMessage(err) : 'Failed to refresh YouTube analytics');
      }
    } finally {
      setIsRefreshingAnalytics(false);
    }
  };

  const StatCard = ({
    label,
    value,
  }: {
    label: string;
    value: string;
  }) => (
    <div className={`rounded-2xl border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {label}
      </div>
      <div className="mt-2 text-base font-semibold">{value}</div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`fixed inset-0 z-50 flex ${isMobileViewport ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'}`}
        style={isMobileViewport ? {
          paddingTop: 'var(--app-mobile-top-offset)',
          paddingBottom: 'var(--app-mobile-bottom-offset)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        } : undefined}
      >
        <div
          className={`flex w-full overflow-hidden shadow-2xl ${panelClassName} ${
            isMobileViewport
              ? 'h-full rounded-none border-0'
              : 'h-[88vh] max-w-6xl rounded-[28px] border'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <aside className={`hidden w-72 flex-col border-r px-5 py-5 md:flex ${isDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50/80'}`}>
            <div className="mb-6 flex items-start gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
                <Settings2 className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Session Settings</h2>
                <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  One place for title, sharing, speakers, playback, and production controls.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {SETTINGS_SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                    activeSection === section.id
                      ? isDarkMode
                        ? 'border-primary-500/50 bg-primary-500/10'
                        : 'border-primary-300 bg-primary-50'
                      : isDarkMode
                        ? 'border-transparent hover:border-gray-700 hover:bg-gray-800/80'
                        : 'border-transparent hover:border-gray-200 hover:bg-white'
                  }`}
                >
                  <div className="text-sm font-semibold">{section.title}</div>
                  <div className={`mt-1 text-xs leading-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {section.description}
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <section className="flex min-w-0 flex-1 flex-col">
            <div className={`flex items-start justify-between gap-4 border-b px-4 py-4 md:px-7 md:py-5 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <div className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">Session</div>
                <h3 className="mt-2 text-xl font-semibold md:text-2xl">{sessionTitle || 'Untitled Session'}</h3>
                <p className={`mt-2 max-w-2xl text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {SETTINGS_SECTIONS.find((section) => section.id === activeSection)?.description}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`rounded-2xl border p-2 transition-colors ${
                  isDarkMode
                    ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                title="Close settings"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {isMobileViewport && (
              <div className={`border-b px-4 py-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50/90'}`}>
                <div className="scrollbar-hide flex min-w-max gap-2 overflow-x-auto pb-1">
                  {SETTINGS_SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        activeSection === section.id
                          ? isDarkMode
                            ? 'bg-primary-600 text-white'
                            : 'bg-primary-100 text-primary-700'
                          : isDarkMode
                            ? 'bg-gray-800 text-gray-300'
                            : 'bg-white text-gray-700'
                      }`}
                    >
                      {section.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-4 py-4 md:px-7 md:py-5">
              {activeSection === 'title' && (
                <div className="space-y-5">
                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Type className="h-5 w-5 text-primary-600" />
                      <div>
                        <h4 className="text-base font-semibold">Session Title</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Keep the session title editorially clean, or seed it from the transcript/YouTube title.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={draftTitle}
                        onChange={(event) => setDraftTitle(event.target.value)}
                        className={inputClassName}
                        placeholder="Session title"
                      />
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={handleSaveTitle}
                          disabled={isSavingTitle || !draftTitle.trim()}
                          className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                        >
                          {isSavingTitle && <Loader2 className="h-4 w-4 animate-spin" />}
                          Save Title
                        </button>
                        <button
                          type="button"
                          onClick={handleGenerateTitle}
                          disabled={isGeneratingTitle}
                          className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                            isDarkMode ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                          } disabled:opacity-50`}
                        >
                          {isGeneratingTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                          Start Title Agent
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Youtube className="h-5 w-5 text-red-500" />
                      <div>
                        <h4 className="text-base font-semibold">YouTube Title Sync</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Pull the current title from the linked YouTube video and optionally keep checking with backoff.
                        </p>
                      </div>
                    </div>

                    {youtubeUrl ? (
                      <div className="space-y-4">
                        <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                          <div><span className="font-medium">Linked video:</span> {youtubeUrl}</div>
                          <div className="mt-2"><span className="font-medium">Last fetched title:</span> {youtubeTitle || 'Not fetched yet'}</div>
                          <div className="mt-1 text-xs text-gray-500">Last sync: {formatSyncTime(youtubeTitleUpdatedAt)}</div>
                          <div className="mt-1 text-xs text-gray-500">Next check: {formatSyncTime(youtubeTitleSync?.nextAttemptAt)}</div>
                          {youtubeTitleSync?.lastError && (
                            <div className="mt-2 text-xs text-red-500">Last error: {youtubeTitleSync.lastError}</div>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={handleSyncYoutubeTitle}
                            disabled={isSyncingYoutubeTitle}
                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                          >
                            {isSyncingYoutubeTitle ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Sync from YouTube
                          </button>
                          <button
                            type="button"
                            onClick={() => { void handleUpdateYoutubeTitleSync(!titleSyncEnabled); }}
                            disabled={isUpdatingTitleSync}
                            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                              titleSyncEnabled
                                ? isDarkMode
                                  ? 'border-brand-yellow bg-brand-yellow/15 text-brand-yellow'
                                  : 'border-[#d8c9a3] bg-[#fff3d6] text-[#7a560c]'
                                : isDarkMode
                                  ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            } disabled:opacity-50`}
                          >
                            {isUpdatingTitleSync ? <Loader2 className="h-4 w-4 animate-spin" /> : titleSyncEnabled ? <CheckCircle2 className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                            {titleSyncEnabled ? 'Disable Auto Sync' : 'Enable Auto Sync'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-brand-yellow/40 bg-brand-yellow/10 text-brand-yellow' : 'border-[#e7c572] bg-[#fff6e5] text-[#7a560c]'}`}>
                        Add a YouTube link in the Playback section before syncing the title.
                      </div>
                    )}
                  </div>

                  {titleError && (
                    <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                      {titleError}
                    </div>
                  )}
                </div>
              )}

              {activeSection === 'share' && (
                <div className="space-y-5">
                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Globe className="h-5 w-5 text-primary-600" />
                      <div>
                        <h4 className="text-base font-semibold">Homepage Visibility</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Control whether this session appears on the public homepage and /shows listing.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                            publishedToHomepage
                              ? isDarkMode ? 'bg-brand-yellow/20 text-brand-yellow' : 'bg-[#FFF4D9] text-[#C98A00]'
                              : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-400'
                          }`}>
                            <Globe className={`h-4 w-4 ${publishedToHomepage ? '' : 'opacity-50'}`} />
                          </div>
                          <div>
                            <span className="text-sm font-medium">
                              {publishedToHomepage ? 'Published' : 'Not published'}
                            </span>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {publishedToHomepage
                                ? 'Visible on the public homepage and /shows page'
                                : 'Hidden from the public homepage and /shows page'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { void handleTogglePublish(); }}
                          disabled={isTogglingPublish}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-wait ${
                            publishedToHomepage
                              ? isDarkMode ? 'bg-brand-yellow' : 'bg-[#C98A00]'
                              : isDarkMode ? 'bg-gray-600' : 'bg-gray-200'
                          } ${isDarkMode ? 'focus:ring-offset-gray-800' : 'focus:ring-offset-white'}`}
                          role="switch"
                          aria-checked={publishedToHomepage}
                          aria-label={publishedToHomepage ? 'Unpublish from homepage' : 'Publish to homepage'}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              publishedToHomepage ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                          {isTogglingPublish && (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <Loader2 className="h-3 w-3 animate-spin text-white" />
                            </span>
                          )}
                        </button>
                      </div>
                      {publishError && (
                        <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                          {publishError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Share2 className="h-5 w-5 text-primary-600" />
                      <div>
                        <h4 className="text-base font-semibold">Share Controls</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Manage public links, permissions, and access from here instead of opening a second dialog.
                        </p>
                      </div>
                    </div>
                    <ShareModal
                      isOpen
                      embedded
                      onClose={() => undefined}
                      sessionId={sessionId}
                      sessionTitle={sessionTitle}
                    />
                  </div>
                </div>
              )}

              {activeSection === 'speakers' && (
                <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                  <div className="mb-4 flex items-center gap-3">
                    <UserCog className="h-5 w-5 text-primary-600" />
                    <div>
                      <h4 className="text-base font-semibold">Speaker Naming</h4>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Rename diarized speakers inline and save without leaving settings.
                      </p>
                    </div>
                  </div>
                  <EditSpeakersModal
                    isOpen
                    embedded
                    onClose={() => undefined}
                    sessionId={sessionId}
                    speakers={speakerIds}
                    speakerSegments={speakerSegments}
                    currentLabels={speakerLabels}
                    onSave={onSaveSpeakerLabels || (async () => undefined)}
                    transcriptionChecksum={transcriptionChecksum}
                    isYouTubeMode={isYouTubeMode}
                  />
                </div>
              )}

              {activeSection === 'playback' && (
                <div className="space-y-5">
                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Video className="h-5 w-5 text-primary-600" />
                      <div>
                        <h4 className="text-base font-semibold">Explicit YouTube Link</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Link a canonical YouTube video for synced playback. When empty, the player falls back to the hosted AWS source.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={videoUrlInput}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setVideoUrlInput(nextValue);
                          setDetectedSource(detectVideoSource(nextValue));
                          setVideoError(null);
                        }}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className={inputClassName}
                      />
                      {videoUrlInput && detectedSource === 'youtube' && (
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          YouTube URL detected.
                        </div>
                      )}
                      {videoUrlInput && detectedSource !== 'youtube' && (
                        <div className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                          Only YouTube URLs belong here.
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => { void handleSaveVideoUrl(); }}
                          disabled={isSavingVideoUrl || (videoUrlInput.trim() !== '' && detectedSource !== 'youtube')}
                          className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                        >
                          {isSavingVideoUrl && <Loader2 className="h-4 w-4 animate-spin" />}
                          Save Playback Link
                        </button>
                        {youtubeUrl && (
                          <button
                            type="button"
                            onClick={() => {
                              setVideoUrlInput('');
                              setDetectedSource(null);
                              void handleSaveVideoUrl('');
                            }}
                            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                              isDarkMode ? 'border-red-700 text-red-300 hover:bg-red-900/20' : 'border-red-300 text-red-700 hover:bg-red-50'
                            }`}
                          >
                            Remove Link
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Youtube className="h-5 w-5 text-red-500" />
                      <div>
                        <h4 className="text-base font-semibold">Current Video Panel Source</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          The left panel now collapses into a slim reopen rail instead of a generic hide state.
                        </p>
                      </div>
                    </div>
                    <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                      <div><span className="font-medium">Playing from:</span> {currentPlaybackLabel}</div>
                      {videoUrl && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="font-medium">URL:</span>
                          <a
                            href={videoUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={videoUrl}
                            className={`inline-flex items-center gap-1 underline-offset-4 hover:underline ${
                              isDarkMode ? 'text-brand-yellow' : 'text-brand-navy'
                            }`}
                          >
                            <span>{formatDisplayUrl(videoUrl) || 'Open source URL'}</span>
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {videoError && (
                    <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                      {videoError}
                    </div>
                  )}
                </div>
              )}

              {activeSection === 'thumbnail' && (
                <div className="space-y-5">
                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Image className="h-5 w-5 text-primary-600" />
                      <div>
                        <h4 className="text-base font-semibold">Current Share Thumbnail</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          This image is used for homepage cards and public/share unfurls. YouTube stays the default until you explicitly save a hosted-video frame.
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-[minmax(0,340px)_1fr]">
                      <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="aspect-[16/10] overflow-hidden">
                          {effectiveThumbnailUrl ? (
                            <img
                              src={effectiveThumbnailUrl}
                              alt="Current session thumbnail"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className={`flex h-full w-full items-center justify-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              No thumbnail yet
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div><span className="font-medium">Source:</span> {effectiveThumbnailSourceLabel}</div>
                        {customThumbnailTimeSecondsState != null && (
                          <div className="mt-1">
                            <span className="font-medium">Captured at:</span> {formatMetricDuration(customThumbnailTimeSecondsState)}
                          </div>
                        )}
                        {thumbnailPreviewVideoUrl ? (
                          <div className={`mt-3 rounded-xl border px-3 py-2 text-xs ${isDarkMode ? 'border-brand-yellow/35 bg-brand-yellow/10 text-brand-yellow' : 'border-[#e7c572] bg-[#fff6e5] text-[#7a560c]'}`}>
                            Hosted playback is available, so you can save a durable frame-based thumbnail below.
                          </div>
                        ) : (
                          <div className={`mt-3 rounded-xl border px-3 py-2 text-xs ${isDarkMode ? 'border-gray-700 bg-gray-950/40 text-gray-400' : 'border-gray-200 bg-white text-gray-500'}`}>
                            Attach or prepare a hosted AWS video if you want to capture a custom frame here.
                          </div>
                        )}
                        {customThumbnailUrlState && (
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => { void handleClearThumbnail(); }}
                              disabled={isClearingThumbnail}
                              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                                isDarkMode ? 'border-red-700 text-red-300 hover:bg-red-900/20' : 'border-red-300 text-red-700 hover:bg-red-50'
                              } disabled:opacity-50`}
                            >
                              {isClearingThumbnail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              Clear Custom Thumbnail
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Film className="h-5 w-5 text-brand-yellow" />
                      <div>
                        <h4 className="text-base font-semibold">Capture Hosted Video Frame</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Scrub through the hosted AWS video, preview the frame, and save it as the session thumbnail.
                        </p>
                      </div>
                    </div>

                    {thumbnailPreviewVideoUrl ? (
                      <div className="space-y-4">
                        <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? 'border-gray-700 bg-black' : 'border-gray-200 bg-gray-100'}`}>
                          <video
                            ref={thumbnailVideoRef}
                            src={thumbnailPreviewVideoUrl}
                            preload="metadata"
                            muted
                            playsInline
                            controls={false}
                            className="aspect-video w-full object-contain"
                            onLoadedMetadata={(event) => {
                              const durationSeconds = Number.isFinite(event.currentTarget.duration)
                                ? event.currentTarget.duration
                                : thumbnailPreviewDuration;
                              const nextSeconds = Math.min(thumbnailFrameSeconds, durationSeconds || thumbnailFrameSeconds);
                              setThumbnailFrameSeconds(nextSeconds);
                              syncThumbnailPreviewToSeconds(nextSeconds);
                            }}
                            onSeeked={() => setThumbnailPreviewReady(true)}
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Preview position</span>
                            <span className="font-medium">
                              {formatMetricDuration(thumbnailFrameSeconds)}
                              {thumbnailPreviewDuration > 0 ? ` / ${formatMetricDuration(thumbnailPreviewDuration)}` : ''}
                            </span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={thumbnailPreviewDuration > 0 ? thumbnailPreviewDuration : 0}
                            step={0.25}
                            value={Math.min(thumbnailFrameSeconds, thumbnailPreviewDuration > 0 ? thumbnailPreviewDuration : thumbnailFrameSeconds)}
                            onChange={handleThumbnailSliderChange}
                            className="w-full accent-primary-600"
                          />
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          <button
                            type="button"
                            onClick={() => { void handleSaveThumbnail(); }}
                            disabled={isSavingThumbnail || !thumbnailPreviewReady}
                            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                          >
                            {isSavingThumbnail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
                            Save This Frame
                          </button>
                          {!thumbnailPreviewReady && (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Seeking preview frame…
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-brand-yellow/40 bg-brand-yellow/10 text-brand-yellow' : 'border-[#e7c572] bg-[#fff6e5] text-[#7a560c]'}`}>
                        Prepare or attach a hosted AWS video first. Once the session has hosted playback, this section becomes a frame picker.
                      </div>
                    )}
                  </div>

                  {thumbnailError && (
                    <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                      {thumbnailError}
                    </div>
                  )}
                </div>
              )}

              {activeSection === 'youtube' && (
                <div className="space-y-5">
                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Youtube className="h-5 w-5 text-red-500" />
                      <div>
                        <h4 className="text-base font-semibold">YouTube Connector</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Connect or reconnect YouTube for analytics fetches, retention sync, and publish-time operations.
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <StatCard
                        label="Connection"
                        value={youtubeAuthLoading ? 'Loading…' : youtubeAuthStatus?.authenticated ? 'Connected' : 'Disconnected'}
                      />
                      <StatCard
                        label="Token expiry"
                        value={youtubeAuthStatus?.tokenExpiry ? formatExpiryCountdown(youtubeAuthStatus.tokenExpiry) : 'Unknown'}
                      />
                      <StatCard
                        label="Read metadata"
                        value={youtubeAuthStatus?.canReadVideoMetadata ? 'Enabled' : 'Unavailable'}
                      />
                      <StatCard
                        label="Edit videos"
                        value={youtubeAuthStatus?.canEditVideos ? 'Enabled' : 'Unavailable'}
                      />
                    </div>

                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => { void startYouTubeAuth(); }}
                        disabled={youtubeAuthBusy || !token}
                        className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                      >
                        {youtubeAuthBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Youtube className="h-4 w-4" />}
                        {youtubeAuthStatus?.authenticated ? 'Reconnect YouTube' : 'Connect YouTube'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { void disconnectYouTubeAuth(); }}
                        disabled={youtubeAuthBusy || !youtubeAuthStatus?.authenticated}
                        className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                          isDarkMode ? 'border-red-700 text-red-300 hover:bg-red-900/20' : 'border-red-300 text-red-700 hover:bg-red-50'
                        } disabled:opacity-50`}
                      >
                        {youtubeAuthBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                        Disconnect
                      </button>
                    </div>

                    {youtubeNotice && (
                      <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-emerald-700/60 bg-emerald-900/20 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                        {youtubeNotice}
                      </div>
                    )}
                    {youtubeAuthError && (
                      <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                        {youtubeAuthError}
                      </div>
                    )}
                  </div>

                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <BarChart3 className="h-5 w-5 text-primary-600" />
                      <div>
                        <h4 className="text-base font-semibold">Session Analytics</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Refresh the linked video’s YouTube stats and retention context. The retention graph updates elsewhere in the session view.
                        </p>
                      </div>
                    </div>

                    {youtubeUrl ? (
                      <>
                        <div className={`mb-4 rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                          <div><span className="font-medium">Linked video:</span> {youtubeUrl}</div>
                          <div className="mt-1"><span className="font-medium">Last analytics fetch:</span> {fetchedAt ? formatSyncTime(fetchedAt) : 'Never'}</div>
                          {videoStats?.videoTitle && (
                            <div className="mt-1"><span className="font-medium">Video title:</span> {videoStats.videoTitle}</div>
                          )}
                        </div>

                        <div className="mb-4 flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={() => { void handleRefreshAnalytics(true); }}
                            disabled={isRefreshingAnalytics || !token}
                            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                          >
                            {isRefreshingAnalytics ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            {videoStats ? 'Refresh YouTube Data' : 'Fetch YouTube Data'}
                          </button>
                        </div>

                        {videoStats ? (
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <StatCard label="Views" value={formatMetricNumber(videoStats.views)} />
                            <StatCard label="Likes" value={formatMetricNumber(videoStats.likes)} />
                            <StatCard label="Comments" value={formatMetricNumber(videoStats.comments)} />
                            <StatCard label="Shares" value={formatMetricNumber(videoStats.shares)} />
                            <StatCard
                              label="Net subscribers"
                              value={formatMetricNumber((videoStats.subscribersGained || 0) - (videoStats.subscribersLost || 0))}
                            />
                            <StatCard
                              label="Watch time"
                              value={videoStats.estimatedMinutesWatched != null ? `${formatMetricNumber(Math.round(videoStats.estimatedMinutesWatched))} min` : '-'}
                            />
                            <StatCard label="Avg view duration" value={formatMetricDuration(videoStats.averageViewDuration)} />
                            <StatCard
                              label="Avg % viewed"
                              value={videoStats.averageViewPercentage != null ? `${videoStats.averageViewPercentage.toFixed(1)}%` : '-'}
                            />
                            <StatCard label="Video length" value={formatMetricDuration(videoDurationSeconds)} />
                            <StatCard
                              label="Published"
                              value={videoStats.videoPublishedAt ? new Date(videoStats.videoPublishedAt).toLocaleDateString() : '-'}
                            />
                          </div>
                        ) : (
                          <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/50 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                            No cached YouTube analytics yet. Fetch the data once the connector is authenticated.
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-brand-yellow/40 bg-brand-yellow/10 text-brand-yellow' : 'border-[#e7c572] bg-[#fff6e5] text-[#7a560c]'}`}>
                        Add a YouTube link in Playback before pulling stats or retention data.
                      </div>
                    )}

                    {youtubeAnalyticsError && (
                      <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                        {youtubeAnalyticsError}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'production' && (
                <div className="space-y-5">
                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Film className="h-5 w-5 text-brand-yellow" />
                      <div>
                        <h4 className="text-base font-semibold">Production Metadata</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Keep Dropbox, timeline, DRP, and local source metadata off the title strip and in one dedicated pane.
                        </p>
                      </div>
                    </div>
                    <ProductionMetadataFields
                      values={{
                        dropboxVideoUrl: prodDropboxVideoUrl,
                        videoFilename: prodVideoFilename,
                        dropboxDrpUrl: prodDropboxDrpUrl,
                        timelineName: prodTimelineName,
                        localVideoPath: prodLocalVideoPath,
                        productionNotes: prodProductionNotes,
                        autoPrepareVideoSource: true,
                      }}
                      onChange={(updates) => {
                        if (updates.dropboxVideoUrl !== undefined) setProdDropboxVideoUrl(updates.dropboxVideoUrl);
                        if (updates.videoFilename !== undefined) setProdVideoFilename(updates.videoFilename);
                        if (updates.dropboxDrpUrl !== undefined) setProdDropboxDrpUrl(updates.dropboxDrpUrl);
                        if (updates.timelineName !== undefined) setProdTimelineName(updates.timelineName);
                        if (updates.localVideoPath !== undefined) setProdLocalVideoPath(updates.localVideoPath);
                        if (updates.productionNotes !== undefined) setProdProductionNotes(updates.productionNotes);
                      }}
                      isDarkMode={isDarkMode}
                      compact
                    />
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => { void persistProductionMetadata(false); }}
                        disabled={isSavingProductionMeta}
                        className="inline-flex items-center gap-2 rounded-xl bg-brand-yellow px-4 py-2 text-sm font-medium text-brand-navy transition-colors hover:bg-[#f0a91a] disabled:opacity-50"
                      >
                        {isSavingProductionMeta && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save Production Metadata
                      </button>
                    </div>
                  </div>

                  <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                    <div className="mb-4 flex items-center gap-3">
                      <Cloud className="h-5 w-5 text-brand-yellow" />
                      <div>
                        <h4 className="text-base font-semibold">AWS Video Source</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Prepare or refresh the hosted playback source that the collapsed video rail reopens.
                        </p>
                      </div>
                    </div>
                    <div className="mb-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => { void refreshVideoSourceStatus(); }}
                        disabled={isRefreshingVideoSource || !token}
                        className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                          isDarkMode ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        } disabled:opacity-50`}
                      >
                        {isRefreshingVideoSource ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh Status
                      </button>
                      <button
                        type="button"
                        onClick={() => { void handlePrepareVideoSource(); }}
                        disabled={isPreparingVideoSource || !token || !(prodDropboxVideoUrl || dropboxVideoUrl || videoSourceStatusState?.sourceAvailable) || awsPrepareSatisfiedByOriginal}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isPreparingVideoSource ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
                        Prepare In AWS
                      </button>
                      <button
                        type="button"
                        onClick={() => { void handleDeleteProxy(); }}
                        disabled={isDeletingProxy || !token || (!videoSourceStatusState?.proxy?.s3Key && !videoSourceStatusState?.proxy?.ready)}
                        className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                          isDarkMode ? 'border-red-700 text-red-300 hover:bg-red-900/20' : 'border-red-300 text-red-700 hover:bg-red-50'
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        {isDeletingProxy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        Delete Proxy
                      </button>
                    </div>

                    {videoSourceStatusState && (
                      <div className={`rounded-2xl border p-4 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div><span className="font-medium">Source:</span> {videoSourceStatusState.s3Source.exists ? 'Uploaded to S3' : (videoSourceStatusState.dropboxVideoUrl ? 'Dropbox URL only' : 'Missing')}</div>
                        <div className="mt-1"><span className="font-medium">Proxy:</span> {videoSourceStatusState.proxy.ready ? 'Ready' : (videoSourceStatusState.proxy.notNeeded ? 'Not needed' : videoSourceStatusState.proxy.status)}</div>
                        {videoSourceStatusState.proxy.fileSize != null && (
                          <div className="mt-1"><span className="font-medium">Proxy size:</span> {formatBytes(videoSourceStatusState.proxy.fileSize)}</div>
                        )}
                        {videoSourceStatusState.playbackKind && (
                          <div className="mt-1"><span className="font-medium">Playback:</span> {videoSourceStatusState.playbackKind}</div>
                        )}
                        {videoSourceStatusState.proxy.errorMessage && (
                          <div className="mt-1 text-red-500">{videoSourceStatusState.proxy.errorMessage}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {(videoFileHash || videoFileSize || videoFrameRate || videoDuration || audioDuration || videoSourceStatusState?.videoMetadata.fileSize || videoSourceStatusState?.videoMetadata.duration || videoSourceStatusState?.videoMetadata.width || videoSourceStatusState?.videoMetadata.codec) && (
                    <div className={`rounded-3xl border p-5 ${panelClassName}`}>
                      <div className="mb-4 flex items-center gap-3">
                        <Copy className="h-5 w-5 text-primary-600" />
                        <div>
                          <h4 className="text-base font-semibold">Video Source Info</h4>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Low-level source metadata for sanity checks.
                          </p>
                        </div>
                      </div>
                      <div className={`rounded-2xl border p-4 text-xs font-mono ${isDarkMode ? 'border-gray-700 bg-gray-900/60 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                        {videoFileHash && (
                          <div className="mb-2 flex items-center gap-2">
                            <span>SHA-256: {videoFileHash.substring(0, 16)}...{videoFileHash.substring(videoFileHash.length - 8)}</span>
                            <button
                              type="button"
                              onClick={() => { void navigator.clipboard.writeText(videoFileHash); }}
                              className={`rounded p-1 transition-colors ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-white'}`}
                              title="Copy full hash"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {(videoFileSize ?? videoSourceStatusState?.videoMetadata.fileSize) != null && (
                          <div>Size: {(((videoFileSize ?? videoSourceStatusState?.videoMetadata.fileSize ?? 0) as number) / (1024 * 1024 * 1024)).toFixed(2)} GB</div>
                        )}
                        {(videoFrameRate ?? videoSourceStatusState?.videoMetadata.frameRate) != null && (
                          <div className="mt-1">FPS: {videoFrameRate ?? videoSourceStatusState?.videoMetadata.frameRate}</div>
                        )}
                        {(videoSourceStatusState?.videoMetadata.width || videoSourceStatusState?.videoMetadata.height) && (
                          <div className="mt-1">Resolution: {videoSourceStatusState?.videoMetadata.width ?? '?'}x{videoSourceStatusState?.videoMetadata.height ?? '?'}</div>
                        )}
                        {videoSourceStatusState?.videoMetadata.codec && (
                          <div className="mt-1">Codec: {videoSourceStatusState.videoMetadata.codec}</div>
                        )}
                        {(videoDuration ?? videoSourceStatusState?.videoMetadata.duration) != null && audioDuration != null && (
                          <div className="mt-1">Duration: {Math.floor(((videoDuration ?? videoSourceStatusState?.videoMetadata.duration) as number) / 60)}:{String(Math.floor(((videoDuration ?? videoSourceStatusState?.videoMetadata.duration) as number) % 60)).padStart(2, '0')} / {Math.floor(audioDuration / 60)}:{String(Math.floor(audioDuration % 60)).padStart(2, '0')}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {productionMetaError && (
                    <div className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-800 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                      {productionMetaError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

    </>
  );
};

export default SessionSettingsModal;
